import { createColumnHelper } from '@tanstack/react-table';
import { TFunction } from 'i18next';
import { BoxIcon } from 'lucide-react';

import { SmileIcon } from '@/components/atoms/Icons/SmileIcon';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { ReplayButton } from '@/components/atoms/ReplayButton/ReplayButton';
import { getIntegrationByType } from '@/components/organisms/Integrations';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Log } from '@/shared/types/analytics';
import { Desk } from '@/shared/types/desk';
import { IntegrationType } from '@/shared/types/integration';
import { getChannelKey } from '@/util/channelName';
import { COLUMN_PRESETS } from '@/util/constants';
import { parseTimestamp } from '@/util/dateUtils';

import { createCsatColumns } from './csatColumns';
import {
  formatSessionDateTime,
  formatDuration,
  getGoalPercentage,
  getSentimentDisplay,
  getUserDisplayName,
} from './utils/sessionUtils';

const columnHelper = createColumnHelper<Log>();

const VISIBLE_TAGS = 2;

const TagBadge = ({ tag }: { tag: string }) => (
  <InfoTooltip content={tag}>
    <span className="min-w-0">
      <Badge type="secondary" flavour="neutral" rounded={false} className="max-w-full truncate">
        {tag}
      </Badge>
    </span>
  </InfoTooltip>
);

/**
 * Creates column definitions for the account sessions table (Human Chat Logs)
 *
 * - Includes Tags column
 * - Excludes custom per-brain dynamic insight columns
 * - System insight columns (goal, sentiment, AI CSAT, conversation summary) are free and always
 *   rendered
 */
export function createAccountSessionColumns(
  t: TFunction,
  onReplayClick: (session: Log) => void,
  desks?: Desk[],
  lastSeen?: string[],
  onPrefetch?: (sessionId: string, startTime?: string) => void
) {
  return [
    // Review (Replay) action column - always visible, first column
    columnHelper.display({
      id: 'review',
      header: () => null,
      enableHiding: false,
      cell: ({ row }) => (
        <ReplayButton
          sessionId={row.original.session_id}
          lastSeen={lastSeen}
          onClick={() => onReplayClick(row.original)}
          onPrefetch={
            onPrefetch
              ? () => onPrefetch(row.original.session_id, row.original.start_time)
              : undefined
          }
        />
      ),
      size: 60,
      minSize: 48,
      maxSize: 72,
      meta: { className: 'text-center' },
    }),
    columnHelper.accessor((row) => row.user_name || row.user_id || '', {
      id: 'user',
      header: () => t('analytics.fields.user'),
      enableHiding: false,
      cell: ({ row }) => {
        const session = row.original;
        return (
          <div className="text-s flex translate-y-[1px] transform flex-col font-medium">
            <span className="block truncate leading-normal">{getUserDisplayName(session)}</span>
            <span className="text-xs">&nbsp;</span>
          </div>
        );
      },
      ...COLUMN_PRESETS.MD,
      meta: { preset: 'MD', label: 'analytics.fields.user' },
    }),
    columnHelper.accessor((row) => parseTimestamp(row.start_time)?.getTime() ?? 0, {
      id: 'date',
      header: () => t('common.date'),
      cell: ({ row }) => {
        const session = row.original;
        const { date, time } = formatSessionDateTime(session.start_time);

        return (
          <div className="flex flex-col">
            <span>{date}</span>
            <span className="text-xs text-foreground-muted">{time}</span>
          </div>
        );
      },
      ...COLUMN_PRESETS.MD,
      meta: { preset: 'MD', label: 'common.date' },
    }),
    columnHelper.accessor('total_user_messages', {
      id: 'info',
      header: () => t('common.info'),
      cell: ({ row }) => {
        const session = row.original;
        const duration = formatDuration(session.start_time, session.end_time, t);

        return (
          <div className="flex flex-col">
            <span>
              {session.total_user_messages}{' '}
              {t('common.message_plural', { count: session.total_user_messages })}
            </span>
            <span className="text-xs text-foreground-muted">
              {duration.value} {duration.unit}
            </span>
          </div>
        );
      },
      ...COLUMN_PRESETS.MD,
      meta: { preset: 'MD', label: 'common.info' },
    }),
    // Environment/Desk column
    columnHelper.accessor((row) => ({ deskId: row.desk_id, channel: row.channel }), {
      id: 'desk',
      header: () => t('common.environment'),
      cell: ({ getValue }) => {
        const { deskId, channel } = getValue() as { deskId: string; channel: IntegrationType };

        const desk = desks?.find((d) => d.desk_id === deskId);
        const Icon = getIntegrationByType(channel?.toLowerCase() as IntegrationType)?.Icon;

        return (
          <div className="flex-start flex flex-col items-center gap-2">
            <span className="flex-start flex w-full items-center gap-1">
              {Icon && <Icon size={16} />}
              {Icon && channel && <span className="text-xs">{t(getChannelKey(channel))}</span>}
            </span>
            <span className="flex-start flex w-full items-center gap-1 text-foreground-muted">
              <BoxIcon size={16} />
              {desk?.name || <span className="pl-6">-</span>}
            </span>
          </div>
        );
      },
      ...COLUMN_PRESETS.MD,
      meta: { preset: 'MD', label: 'common.environment' },
    }),
    // Attributes column
    columnHelper.accessor(
      (row) => ({ channel: row.channel, isContained: row.is_contained, isCovered: row.is_covered }),
      {
        id: 'attributes',
        header: () => t('analytics.fields.attributes'),
        cell: ({ getValue }) => {
          const attrs = getValue() as { channel: string; isContained: boolean; isCovered: boolean };
          return (
            <div className="flex flex-col items-start gap-1">
              <Badge
                type="secondary"
                flavour={attrs.isCovered ? 'successful' : 'warning'}
                rounded={false}
              >
                {attrs.isCovered
                  ? t('analytics.charts_tooltip.cover')
                  : t('analytics.charts_tooltip.not_cover')}
              </Badge>
              <Badge
                type="secondary"
                flavour={attrs.isContained ? 'successful' : 'warning'}
                rounded={false}
              >
                {attrs.isContained
                  ? t('analytics.charts_tooltip.contain')
                  : t('analytics.charts_tooltip.not_contain')}
              </Badge>
            </div>
          );
        },
        ...COLUMN_PRESETS.MD,
        meta: { preset: 'MD', label: 'analytics.fields.attributes' },
      }
    ),
    // System insight columns (non-dynamic)
    columnHelper.accessor('goal_achieved', {
      id: 'goal',
      header: () => t('common.goal'),
      cell: ({ getValue }) => {
        const goalAchieved = getValue() as string | null;
        if (goalAchieved === '0' || !goalAchieved) {
          return (
            <InfoTooltip
              content={t('analytics.empty_tooltip', { 0: t('analytics.fields.ai_agent_goal') })}
              size="auto"
            >
              <span className="block w-12 text-start">-</span>
            </InfoTooltip>
          );
        }
        return (
          <InfoTooltip content={getGoalPercentage(goalAchieved, t).tooltip} size="auto">
            <span>
              <Progress
                className="max-w-[82%] bg-surface-button-secondary"
                value={getGoalPercentage(goalAchieved, t).value}
              />
            </span>
          </InfoTooltip>
        );
      },
      ...COLUMN_PRESETS.MD,
      meta: { preset: 'MD', label: 'common.goal' },
    }),
    columnHelper.accessor('user_sentiment', {
      id: 'sentiment',
      header: () => t('analytics.fields.sentiment'),
      cell: ({ getValue }) => {
        const user_sentiment = getValue() as string | null;
        if (user_sentiment === '0' || !user_sentiment) {
          return (
            <InfoTooltip
              content={t('analytics.empty_tooltip', { 0: t('analytics.fields.user_sentiment') })}
              size="auto"
            >
              <div className="flex w-fit items-center justify-start">-</div>
            </InfoTooltip>
          );
        }

        const sentimentDisplay = getSentimentDisplay(user_sentiment, t);
        if (sentimentDisplay) {
          return (
            <InfoTooltip content={sentimentDisplay.tooltip} size="auto">
              <div className="flex w-fit items-center justify-start">
                <SmileIcon variant={sentimentDisplay.variant} />
              </div>
            </InfoTooltip>
          );
        }
        return <div className="flex items-center justify-start">-</div>;
      },
      ...COLUMN_PRESETS.MD,
      meta: { preset: 'MD', label: 'analytics.fields.sentiment' },
    }),

    ...createCsatColumns(t),

    // Conversation summary column
    columnHelper.accessor('conversation_summary', {
      id: 'summary',
      header: () => t('analytics.fields.summary'),
      cell: ({ getValue }) => {
        const summary = getValue() as string | null;
        if (!summary) {
          return (
            <InfoTooltip content={t('analytics.no_summary')} showArrow>
              <span className="block w-fit">-</span>
            </InfoTooltip>
          );
        }
        return (
          <InfoTooltip content={summary} size="large" showArrow>
            {/* whitespace-normal overrides the table cell's nowrap so the clamp can wrap */}
            <p className="line-clamp-2 text-start whitespace-normal">{summary}</p>
          </InfoTooltip>
        );
      },
      ...COLUMN_PRESETS.LG,
      meta: { preset: 'LG', label: 'analytics.fields.summary' },
    }),
    // Tags column (Account logs only)
    columnHelper.accessor('tags', {
      id: 'tags',
      header: () => t('analytics.fields.tags'),
      cell: ({ getValue }) => {
        const tags = (getValue() as string[] | null) || [];
        if (!tags.length) {
          return <div className="flex w-full items-center justify-start">-</div>;
        }

        const visibleTags = tags.slice(0, VISIBLE_TAGS);
        const hiddenCount = tags.length - visibleTags.length;

        return (
          <div className="flex w-full min-w-0 flex-nowrap items-center gap-1 overflow-hidden">
            {visibleTags.map((tag) => (
              <TagBadge key={tag} tag={tag} />
            ))}
            {hiddenCount > 0 && (
              <InfoTooltip
                size="auto"
                content={
                  <div className="flex max-h-64 flex-col gap-1 overflow-y-auto">
                    {tags.slice(VISIBLE_TAGS).map((tag) => (
                      <span key={tag}>{tag}</span>
                    ))}
                  </div>
                }
              >
                <span className="shrink-0">
                  <Badge type="secondary" flavour="neutral" rounded={false}>
                    +{hiddenCount}
                  </Badge>
                </span>
              </InfoTooltip>
            )}
          </div>
        );
      },
      ...COLUMN_PRESETS.MD,
      meta: { preset: 'MD', label: 'common.tags' },
    }),
  ];
}
